import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, FileText, CreditCard, MessagesSquare, Car, CarFront, Truck, Check, Zap } from "lucide-react";
import { Nav, scrollToId } from "@/components/Nav";
import { Marquee } from "@/components/landing/Marquee";
import { DtcDecoder } from "@/components/landing/DtcDecoder";
import { Footer, WhatsAppFloat } from "@/components/landing/Footer";
import { ExpertCard } from "@/components/ExpertCard";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1776775673105-0c9e3214fa4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxjYXIlMjB2YW4lMjB0cnVjayUyMGZsZWV0JTIwZGFyayUyMGF1dG9tb3RpdmV8ZW58MHx8fHwxNzkyMDEwMzczfDA&ixlib=rb-4.1.0&q=85";

const HERO_LINES = [
  { text: "BLOCCATO SU", cls: "text-white" },
  { text: "UNA DIAGNOSI?", cls: "text-stroke-volt" },
  { text: "UN ESPERTO", cls: "text-white" },
  { text: "TI SBLOCCA.", cls: "text-volt" },
];

const STATS = [
  { icon: Car, value: "Auto", label: "Furgoni · Camion" },
  { icon: Zap, value: "da €35", label: "3 livelli di consulenza" },
  { icon: MessagesSquare, value: "Chat", label: "diretta con l'esperto" },
];

const STEPS = [
  {
    n: "01",
    icon: FileText,
    title: "Apri il ticket",
    desc: "Descrivi il problema, aggiungi codici errore DTC e foto del veicolo. Auto, furgone o camion: vale tutto.",
  },
  {
    n: "02",
    icon: CreditCard,
    title: "Scegli il livello e paga",
    desc: "Base €35, Avanzato €60 o Premium €100. Pagamento sicuro con carta o PayPal, direttamente in piattaforma.",
  },
  {
    n: "03",
    icon: MessagesSquare,
    title: "L'esperto ti guida",
    desc: "Un tecnico accetta il tuo ticket e ti risponde in chat in tempo reale, in videochiamata o via TeamViewer.",
  },
];

export const LEVELS = [
  {
    id: "base",
    label: "Base",
    price: 35,
    tagline: "La risposta scritta dell'esperto",
    features: [
      "Analisi scritta del problema",
      "Piano d'azione con costi stimati",
      "Chat diretta con l'esperto",
      "Risposta entro 24 ore",
    ],
  },
  {
    id: "avanzato",
    label: "Avanzato",
    price: 60,
    tagline: "Videochiamata con il tecnico",
    highlight: true,
    features: [
      "Tutto il livello Base",
      "Videochiamata di 30 minuti",
      "Lettura guidata codici OBD2",
      "Presa in carico prioritaria",
    ],
  },
  {
    id: "premium",
    label: "Premium",
    price: 100,
    tagline: "Diagnosi completa da remoto",
    features: [
      "Tutto il livello Avanzato",
      "Sessione TeamViewer sul tuo PC diagnostico",
      "Report tecnico scritto",
      "Supporto esteso 7 giorni",
    ],
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [experts, setExperts] = useState([]);
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 220]);

  useEffect(() => {
    api.get("/experts").then((r) => setExperts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const ctaTicket = user ? (user.role === "cliente" ? "/nuovo-ticket" : "/dashboard") : "/accedi?mode=register";

  return (
    <div className="bg-obsidian min-h-screen" data-testid="home-page">
      <Nav />

      <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
          <img
            src={HERO_IMG}
            alt="Auto, furgoni e camion al tramonto"
            className="w-full h-[120%] object-cover object-center opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/40 to-obsidian" />
          <div className="absolute inset-0 bg-grid" />
          <div className="absolute inset-0 hero-glow" />
        </motion.div>

        <div className="w-full px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-16 pt-40">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="eyebrow mb-6"
            data-testid="hero-overline"
          >
            Marketplace di Consulenza Tecnica · Auto · Furgoni · Camion
          </motion.p>

          <h1 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-[1.7rem] sm:text-5xl lg:text-7xl">
            {HERO_LINES.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                  className={`block ${l.cls}`}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
            data-testid="hero-subheadline"
          >
            Meccanici, elettrauti e automobilisti in difficoltà aprono un ticket con foto e codici
            errore: un tecnico esperto lo accetta e li guida alla soluzione, anche da remoto.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => navigate(ctaTicket)}
              className="bg-volt text-obsidian font-semibold px-7 py-3.5 rounded-full hover:bg-spark hover:scale-[1.03] transition-all duration-200 shadow-[0_0_32px_rgba(0,240,255,0.35)]"
            >
              Apri un Ticket — da €35
            </button>
            <button
              data-testid="hero-cta-expert"
              onClick={() => navigate("/accedi?mode=register&role=esperto")}
              className="border border-white/20 text-slate-200 px-7 py-3.5 rounded-full hover:border-spark hover:text-spark hover:scale-[1.03] transition-all duration-200"
            >
              Diventa Esperto
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-14 grid grid-cols-3 gap-4 max-w-xl border-t border-white/10 pt-8"
          >
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <s.icon size={18} className="text-volt mb-2" />
                <div className="font-display text-xl sm:text-3xl font-bold text-white">{s.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate-500" aria-hidden="true">
          <ArrowDown size={18} className="animate-bounce" />
        </div>
      </section>

      <Marquee />

      <section id="come-funziona" data-testid="how-section" className="py-20 lg:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="mb-14">
          <p className="eyebrow mb-4">Come Funziona</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-2xl">
            Dal problema alla soluzione, <span className="text-volt">in tre mosse.</span>
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.12 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              data-testid={`how-step-${s.n}`}
              className="glass-card rounded-2xl p-6 sm:p-8 relative overflow-hidden group"
            >
              <span className="absolute -top-4 -right-2 font-display font-extrabold text-[90px] leading-none text-white/[0.04] group-hover:text-volt/10 transition-colors duration-300 select-none">
                {s.n}
              </span>
              <span className="font-mono text-xs text-spark tracking-[0.25em]">{s.n}</span>
              <s.icon size={24} className="text-volt mt-5 mb-4" />
              <h3 className="font-display text-lg sm:text-xl font-semibold text-white">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="livelli" data-testid="levels-section" className="py-20 lg:py-28 bg-[#0E1017] border-y border-white/5">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="eyebrow mb-4">Livelli di Consulenza</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
                Quanto aiuto ti serve?
              </h2>
            </div>
            <p className="font-mono text-xs text-slate-500 uppercase tracking-[0.2em]">
              Paghi solo quando apri il ticket
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {LEVELS.map((l, i) => (
              <motion.article
                key={l.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.12 }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                data-testid={`level-card-${l.id}`}
                className={`rounded-2xl p-6 sm:p-8 flex flex-col relative ${
                  l.highlight
                    ? "bg-volt/[0.07] border border-volt/40 shadow-[0_0_40px_rgba(0,240,255,0.12)]"
                    : "glass-card"
                }`}
              >
                {l.highlight && (
                  <span className="absolute -top-3 left-6 font-mono text-[10px] uppercase tracking-[0.2em] bg-volt text-obsidian font-bold px-3 py-1 rounded-full">
                    Più scelto
                  </span>
                )}
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">{l.label}</p>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className={`font-display text-4xl sm:text-5xl font-extrabold ${l.highlight ? "text-volt" : "text-white"}`}>
                    €{l.price}
                  </span>
                  <span className="text-slate-500 text-sm">/ ticket</span>
                </div>
                <p className="mt-2 text-sm text-slate-400">{l.tagline}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {l.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check size={15} className="text-volt mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`level-cta-${l.id}`}
                  onClick={() => navigate(user?.role === "cliente" ? `/nuovo-ticket?level=${l.id}` : ctaTicket)}
                  className={`mt-8 w-full py-3.5 rounded-full font-semibold text-sm transition-all duration-200 hover:scale-[1.02] ${
                    l.highlight
                      ? "bg-volt text-obsidian hover:bg-spark"
                      : "border border-white/20 text-slate-200 hover:border-volt hover:text-volt"
                  }`}
                >
                  Apri ticket {l.label}
                </button>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section id="esperti-preview" data-testid="experts-preview-section" className="py-20 lg:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="mb-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="eyebrow mb-4">Gli Esperti</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-xl">
              Tecnici veri, <span className="text-stroke-volt">pronti a rispondere.</span>
            </h2>
          </div>
          <Link
            to="/esperti"
            data-testid="experts-directory-link"
            className="font-mono text-xs uppercase tracking-[0.2em] text-volt hover:text-spark transition-colors"
          >
            Tutti gli esperti →
          </Link>
        </motion.div>

        {experts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {experts.map((e) => (
              <ExpertCard key={e.user_id} expert={e} testid={`expert-card-${e.user_id}`} />
            ))}
          </div>
        ) : (
          <motion.div {...fadeUp} className="glass-card rounded-2xl p-8 sm:p-12 text-center" data-testid="experts-empty">
            <Truck size={28} className="text-volt mx-auto mb-4" />
            <h3 className="font-display text-xl font-semibold text-white">I primi esperti si stanno registrando</h3>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
              Sei un tecnico specializzato? Registrati come esperto: la piattaforma trattiene solo
              il 20% di commissione, l'80% di ogni consulenza è tuo.
            </p>
            <button
              data-testid="experts-empty-cta"
              onClick={() => navigate("/accedi?mode=register&role=esperto")}
              className="mt-6 border border-spark/50 text-spark font-semibold text-sm px-6 py-3 rounded-full hover:bg-spark hover:text-obsidian transition-all duration-200"
            >
              Diventa Esperto
            </button>
          </motion.div>
        )}
      </section>

      <DtcDecoder />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
